/* -------------------------------------------------------------------------- */
// Filter definition used by the dashboard configure form:
// { key, label, type, required, options?: [{ value, label }],
//   source?: { url, value_field, label_field } }
// Mirrors widget.available_filters[].type defined in the backoffice.
export const FILTER_TYPES = {
  TEXT: "text",
  NUMBER: "number",
  BOOLEAN: "boolean",
  DATE: "date",
  DATE_RANGE: "date_range",
  SELECT: "select",
  MULTI_SELECT: "multi_select",
};

const KNOWN_TYPES = Object.values(FILTER_TYPES);

// The API base URL already ends with /api; service endpoints may repeat it.
export const toApiPath = (endPoint) =>
  (endPoint || "").replace(/^\/?api(?=\/)/, "");

// Service filters use free-form types ("string", ...) and load their choices
// from data_source; map them onto the form's filter types.
const fromServiceFilter = (filter) => {
  const url = toApiPath(filter.data_source?.end_point);
  const type = KNOWN_TYPES.includes(filter.type)
    ? filter.type
    : url
    ? FILTER_TYPES.SELECT
    : FILTER_TYPES.TEXT;
  return {
    key: filter.key,
    label: filter.label || filter.key,
    type,
    required: Boolean(filter.required),
    ...(url && { source: { url } }),
  };
};

// The backfill service filters are the base; widget.available_filters
// override a service filter with the same key or add new ones.
export const resolveWidgetFilters = (widget) => {
  const serviceFilters = (widget?.backfill_service?.filters || [])
    .filter((filter) => filter?.key)
    .map(fromServiceFilter);
  const overrides = (widget?.available_filters || []).filter(
    (filter) => filter?.key
  );
  const overrideKeys = new Set(overrides.map((filter) => filter.key));
  const merged = serviceFilters.map((filter) => {
    if (!overrideKeys.has(filter.key)) return filter;
    const override = overrides.find(({ key }) => key === filter.key);
    // Static options in the override replace the service's option source.
    const { source, ...rest } = filter;
    return override.options?.length && !override.source?.url
      ? { ...rest, ...override }
      : { ...filter, ...override };
  });
  const serviceKeys = new Set(serviceFilters.map((filter) => filter.key));
  return [
    ...merged,
    ...overrides.filter((filter) => !serviceKeys.has(filter.key)),
  ];
};

// Saved filter values -> request params: date ranges become from/to,
// multi selects a comma-separated list.
export const filtersToParams = (values = {}) =>
  Object.entries(values).reduce((acc, [key, value]) => {
    if (value === undefined || value === null || value === "") return acc;
    if (Array.isArray(value))
      return value.length ? { ...acc, [key]: value.join(",") } : acc;
    if (typeof value === "object")
      return {
        ...acc,
        ...(value.from && { from: value.from }),
        ...(value.to && { to: value.to }),
      };
    return { ...acc, [key]: value };
  }, {});
